import { motion } from "framer-motion";
import { Instagram, Sparkles } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6 },
  }),
};

export default function ClothingPage() {
  return (
    <div className="pt-28 lg:pt-44" data-testid="clothing-page">
      {/* Hero */}
      <section className="py-10 md:py-14 bg-warmcream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-accent text-2xl text-sunset mb-1">comfort first</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-3">
            Clothing
          </h1>
          <p className="text-charcoal/60 max-w-xl leading-relaxed">
            Handmade, one-of-a-kind pieces designed for movement, freedom and feeling amazing.
            Every piece is unique — just like you.
          </p>
        </div>
      </section>

      {/* Signature Piece */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={0}
            >
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_high-on-life-1/artifacts/es82m8sl_664BE88C-A36D-43DB-B08B-F98CD462A848-308-000000087EB9114D.jpg"
                  alt="Jax full length in baggy trousers"
                  className="rounded-3xl w-full aspect-[3/4] object-cover"
                />
                <div className="absolute -bottom-4 -right-4 bg-olive text-white rounded-2xl px-6 py-4 shadow-lg">
                  <p className="font-display text-2xl">&pound;85</p>
                  <p className="text-xs text-white/70">per pair</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
            >
              <Sparkles className="text-sunset mb-4" size={28} />
              <p className="font-accent text-xl text-sunset mb-2">signature piece</p>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">
                Handmade Baggy Trousers
              </h2>
              <div className="space-y-4 text-charcoal/70 leading-relaxed">
                <p>
                  Each pair of baggy trousers is individually handmade — no two pairs are ever the same.
                  Designed for comfort, movement, dancing, surfing and living your best life.
                </p>
                <p>
                  Street-inspired, loose-fit and made for real life. Whether you're
                  hitting the beach, the gym, the dance floor, or just living your day — these trousers are
                  your new go-to.
                </p>
                <p className="text-black font-medium">
                  Every pair is unique. Every pair tells a story.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <span className="text-xs bg-sage/10 text-sage px-3 py-1.5 rounded-full font-medium">#handmade</span>
                <span className="text-xs bg-sage/10 text-sage px-3 py-1.5 rounded-full font-medium">#unique</span>
                <span className="text-xs bg-sage/10 text-sage px-3 py-1.5 rounded-full font-medium">#comfort</span>
                <span className="text-xs bg-sage/10 text-sage px-3 py-1.5 rounded-full font-medium">#streetwear</span>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="https://www.instagram.com/lazyelley/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-olive text-white hover:bg-amber rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-0.5"
                  data-testid="clothing-main-instagram"
                >
                  <Instagram size={16} /> @lazyelley
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-12 md:py-16 bg-peachlight">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-accent text-xl text-sunset mb-2">stay tuned</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">
            More Coming Soon
          </h2>
          <p className="text-charcoal/60 max-w-lg mx-auto mb-8">
            New pieces are always in the works. Follow @lazyelley on Instagram to see new drops, behind-the-scenes
            sewing sessions and styling ideas.
          </p>
          <a
            href="https://www.instagram.com/lazyelley/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-ocean text-white hover:bg-charcoal rounded-full px-8 py-3.5 font-medium transition-all duration-300"
            data-testid="clothing-follow-btn"
          >
            <Instagram size={16} /> Follow @lazyelley
          </a>
        </div>
      </section>
    </div>
  );
}
